import { Chat } from "@/components/CodeGen/Chat/Chat";
import { ErrorScreen } from "@/components/ErrorScreen";
import { CodeGenLayout } from "@/components/layout/codegen-layout";
import { useAutoResizeTextarea, useInputFocus } from "@/hooks";
import { useActiveChatId } from "@/lib/hooks/useActiveChatId";
import { listChatsQueryOptions } from "@/lib/hooks/useChats";
import { useEventsWebSocket } from "@/lib/hooks/useEventsWebSocket";
import { useInputStore, useMessageStore, usePanelStore } from "@/state/stores";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";

export const Route = createFileRoute("/(codegen)/codegen")({
  component: Layout,
  loader: async ({ context: { queryClient } }) => {
    const result = await queryClient.ensureQueryData(listChatsQueryOptions());
    return {
      chats: result.data.chats,
    };
  },
  errorComponent: ({ error }) => {
    return (
      <ErrorScreen
        error={error}
        title="Error loading Chats"
        message="An unknown error occurred, which is our least favorite kind of error."
      />
    );
  },
});

function Layout() {
  useEventsWebSocket();

  const setMessageLoading = useMessageStore((state) => state.setLoading);
  const resetMessages = useMessageStore((state) => state.resetMessages);
  const clearInput = useInputStore((state) => state.clearInput);
  const { openPanel, closePanel } = usePanelStore();

  const chatId = useActiveChatId();
  const { resetHeight } = useAutoResizeTextarea();
  const focusInput = useInputFocus();

  useEffect(() => {
    resetHeight();

    // Focus the input if we're on a new chat page. Otherwise, the input focus
    // determination will be handled by Chat.tsx (depending on loading messages,
    // thinking, etc.)
    if (!chatId) {
      focusInput();
      closePanel();
      return;
    }

    openPanel("suggestions");
  }, [chatId, closePanel, focusInput, openPanel, resetHeight]);

  useEffect(() => {
    setMessageLoading(false);
    resetMessages();
    clearInput();
  }, [clearInput, resetMessages, setMessageLoading]);

  return (
    <CodeGenLayout>
      <Chat>
        <Outlet />
      </Chat>
    </CodeGenLayout>
  );
}
